import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Heading, IconBin, IconPlus, Text } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUpdateSuggestion } from "@/api/mutations";
import { apiCall } from "@/api/shared";
// @ts-expect-error - Table.jsx is not typed yet
import Table from "@/components/Table";
import { useUIStateStore } from "@/store/uiStateStore";

import type { BackendAlbumResponse } from "./spotify/backendMappers";

import useMultiSongStore from "./album/store/multiSongStore";
import { useWizardStore } from "./album/store/wizardStore";
import { mapBackendAlbum, mapBackendTrack } from "./spotify/backendMappers";

type AlbumSuggestion = {
  spotify_artist_id: string;
  spotify_album_id: string;
  album_title: string;
  album_type: string;
  total_tracks: number;
  artwork_url: string;
  release_date: string;
};

type AlbumSuggestionsTableProps = {
  data: AlbumSuggestion[];
  highlightColour?: string;
  spotifyArtistId?: string;
};

type CellProps = {
  row: {
    values: AlbumSuggestion;
    original: AlbumSuggestion;
  };
};

function AlbumTitleCell({ row }: CellProps) {
  const image = row?.original?.artwork_url;
  const title = row?.original?.album_title;

  return (
    <div className="flex items-center gap-3">
      {image && (
        <div className="flex-shrink-0 w-10 h-10">
          <img
            alt="album artwork"
            title="album artwork"
            src={image}
            className="object-cover w-10 h-10 rounded"
          />
        </div>
      )}
      <span className="flex-1">{title}</span>
    </div>
  );
}

function ReleaseDateCell({ row }: CellProps) {
  return row?.values?.release_date
    ? format(new Date(row.values.release_date), "yyyy-MM-dd")
    : null;
}

function TracksCell({ row }: CellProps) {
  const count = row?.values?.total_tracks;
  return count ? `${count} ${count === 1 ? "track" : "tracks"}` : null;
}

function AlbumTypeCell({ row }: CellProps) {
  const type = row?.values?.album_type;
  // Capitalize first letter
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : null;
}

type AlbumActionsCellProps = CellProps & {
  onAddAlbum: (albumId: string) => void;
  onDismissAlbum: (album: AlbumSuggestion) => void;
};

function AlbumActionsCell({ row, onAddAlbum, onDismissAlbum }: AlbumActionsCellProps) {
  const { t } = useTranslation(["album"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const flags = useFlags();

  return (
    <div className="flex justify-end gap-2 p-2">
      {flags["pd-add-album-feature"] === true && (
        <Button
          variant={isTunecore ? "primary" : "secondary"}
          className="w-auto h-auto p-2 text-xs group"
          tooltip
          tooltipText={t("album:add-album") ?? "Add Album"}
          label={<IconPlus className={`w-4 h-4 ${isTunecore ? "stroke-white" : "stroke-black"}`} />}
          onClick={() => void onAddAlbum(row.original.spotify_album_id)}
        />
      )}
      <Button
        variant={isTunecore ? "secondary" : "primary"}
        className={`w-auto h-auto p-2 text-xs group ${isTunecore ? "ml-2 px-5 ring-1" : "bg-sentric-mid-grey border-0"}`}
        tooltip
        tooltipText={t("album:dismiss-album") ?? "Remove Suggestion"}
        label={<IconBin className={`w-4 h-4 ${isTunecore ? "stroke-white" : "stroke-black"}`} />}
        onClick={() => onDismissAlbum(row.original)}
      />
    </div>
  );
}

function AlbumSuggestionsTable({ data, highlightColour, spotifyArtistId }: AlbumSuggestionsTableProps) {
  const { t } = useTranslation(["songs", "common", "album"]);
  const navigate = useNavigate();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { setSourceData, setTracks } = useMultiSongStore();
  const { goToStep, markStepComplete, resetWizard } = useWizardStore();
  const queryClient = useQueryClient();
  const updateSuggestionMutation = useUpdateSuggestion();

  // State for delete confirmation dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState<AlbumSuggestion | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissProgress, setDismissProgress] = useState({ current: 0, total: 0 });

  // Handler to open delete confirmation dialog
  const handleDismissAlbum = useCallback((album: AlbumSuggestion) => {
    setAlbumToDelete(album);
    setIsDeleteDialogOpen(true);
  }, []);

  // Handler to confirm dismissal - fetch tracks via backend and update their suggestion status
  const confirmDismissAlbum = useCallback(async () => {
    if (!albumToDelete)
      return;

    setIsDismissing(true);
    setDismissProgress({ current: 0, total: 0 });

    try {
      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/album/${albumToDelete.spotify_album_id}`,
      }) as BackendAlbumResponse;

      const allTracks = response.album.tracks ?? [];

      // Set total for progress tracking
      setDismissProgress({ current: 0, total: allTracks.length });

      // Update each track's suggestion status to dismissed (status: 2)
      let processedCount = 0;
      for (const track of allTracks) {
        if (track.id) {
          await new Promise<void>((resolve) => {
            updateSuggestionMutation.mutate(
              { spotify_song_id: track.id, status: 2 }, // status 2 = dismissed
              {
                onSuccess: () => {
                  processedCount++;
                  setDismissProgress(prev => ({ ...prev, current: processedCount }));
                  resolve();
                },
                onError: () => {
                  console.warn(`Failed to dismiss suggestion for track ${track.id}`);
                  processedCount++;
                  setDismissProgress(prev => ({ ...prev, current: processedCount }));
                  resolve();
                },
              },
            );
          });
        }
      }

      // Invalidate album suggestions query to refresh the list - await to ensure it completes
      await queryClient.invalidateQueries({ queryKey: ["album-suggestions", spotifyArtistId] });

      toast.success(t("album:album-dismissed") ?? "Album suggestion removed");
      setIsDeleteDialogOpen(false);
      setAlbumToDelete(null);
    }
    catch (error) {
      console.error("Error dismissing album:", error);
      toast.error(error instanceof Error ? error.message : "Failed to remove album suggestion");
    }
    finally {
      setIsDismissing(false);
      setDismissProgress({ current: 0, total: 0 });
    }
  }, [albumToDelete, updateSuggestionMutation, queryClient, spotifyArtistId, t]);

  // Function to fetch album data via backend and navigate to select songs step
  const handleAddAlbum = useCallback(async (spotifyAlbumId: string) => {
    try {
      toast.loading("Loading album...");

      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/album/${spotifyAlbumId}`,
      }) as BackendAlbumResponse;

      const albumInfoData = mapBackendAlbum(response.album);
      const tracks = (response.album.tracks ?? []).map(mapBackendTrack);

      setSourceData(albumInfoData);
      setTracks(tracks);

      resetWizard();
      markStepComplete("import");
      goToStep("select-songs");

      void navigate({ to: "/songs/create-album" });

      toast.dismiss();
      toast.success("Album loaded successfully!");
    }
    catch (error) {
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : "Failed to load album");
      console.error("Error loading album:", error);
    }
  }, [setSourceData, setTracks, goToStep, markStepComplete, resetWizard, navigate]);

  // Memoize the cell renderer function to avoid nested component definition
  const renderActionsCell = React.useCallback(
    (props: CellProps) => (
      <AlbumActionsCell
        {...props}
        onAddAlbum={(albumId: string) => {
          void handleAddAlbum(albumId);
        }}
        onDismissAlbum={handleDismissAlbum}
      />
    ),
    [handleAddAlbum, handleDismissAlbum],
  );

  const columns = React.useMemo(
    () => [
      {
        Header: t("songs:album-title-col-header") ?? "Album Title",
        accessor: "album_title",
        width: "30%",
        Cell: AlbumTitleCell,
      },
      {
        Header: t("songs:album-type-col-header") ?? "Album Type",
        accessor: "album_type",
        width: "15%",
        Cell: AlbumTypeCell,
      },
      {
        Header: t("songs:release-date"),
        accessor: "release_date",
        width: "15%",
        Cell: ReleaseDateCell,
      },
      {
        Header: t("songs:album-tracks-col-header") ?? "Tracks",
        accessor: "total_tracks",
        width: "15%",
        Cell: TracksCell,
      },
      {
        Header: "",
        accessor: "spotify_album_id",
        width: "15%",
        Cell: renderActionsCell,
      },
    ],
    [t, renderActionsCell],
  );

  return (
    <>
      <Table
        enableColumnResizing={false}
        data={data.sort((a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime())}
        sortBy={{}}
        columns={columns}
        highlightColour={highlightColour}
        searchSuffix="albums"
        min={5}
        getRowId={React.useCallback((row: AlbumSuggestion) => row.spotify_album_id, [])}
        noneMessage="No album suggestions found"
        disableRowHover
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          // Prevent closing modal while dismissing
          if (!open && !isDismissing) {
            setIsDeleteDialogOpen(false);
            setAlbumToDelete(null);
          }
        }}
      >
        <DialogContent className={isTunecore ? "bg-tunecore-dark-element text-white border-tunecore-mid-grey" : ""}>
          <DialogHeader>
            <DialogTitle asChild>
              <Heading as="h3">
                {isDismissing
                  ? (t("album:dismissing-album") ?? "Removing Suggestion...")
                  : (t("album:confirm-dismiss") ?? "Remove Album Suggestion")}
              </Heading>
            </DialogTitle>
            <DialogDescription asChild>
              {isDismissing
                ? (
                    <div className="space-y-3">
                      <Text as="p">
                        {t("album:dismissing-tracks", { current: dismissProgress.current, total: dismissProgress.total })
                          ?? `Processing ${dismissProgress.current} of ${dismissProgress.total} tracks...`}
                      </Text>
                      {/* Progress bar */}
                      <div className="w-full h-2 overflow-hidden rounded-full bg-sentric-mid-grey dark:bg-sentric-dark-grey">
                        <div
                          className="h-full transition-all duration-300 ease-out bg-sentric-orange"
                          style={{
                            width: dismissProgress.total > 0
                              ? `${(dismissProgress.current / dismissProgress.total) * 100}%`
                              : "0%",
                          }}
                        />
                      </div>
                    </div>
                  )
                : (
                    <Text as="p">
                      {t("album:dismiss-album-confirmation", { albumTitle: albumToDelete?.album_title })
                        ?? `Are you sure you want to remove "${albumToDelete?.album_title ?? "this album"}" from your suggestions? This will remove all ${albumToDelete?.total_tracks ?? 0} tracks.`}
                    </Text>
                  )}
            </DialogDescription>
          </DialogHeader>
          {!isDismissing && (
            <DialogFooter className="mt-4">
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                label={t("common:cancel") ?? "Cancel"}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setAlbumToDelete(null);
                }}
                disabled={isDismissing}
              />
              <Button
                variant={isTunecore ? "primary" : "secondary"}
                label={t("album:dismiss") ?? "Remove"}
                onClick={() => void confirmDismissAlbum()}
                disabled={isDismissing || !albumToDelete}
              />
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AlbumSuggestionsTable;
